import React, { useCallback } from "react";
import SalesDocDetails from "../../_components/Sales/SalesDocDetails";
import { FiHash, FiCalendar } from "react-icons/fi";
import { FieldInput, InfoRow } from "../../_components/Sales/SalesShared";
import {
    getSalesOrderById,
    updateSalesOrder,
    createSalesOrder,
    deleteSalesOrder,
} from "../../_services/sales.service";
import { getActiveCustomerCompanyNames, getCustomersByName } from "../../_services/contact.service";
import { companyProfileService } from "../../_services/companyProfile.service";
import { isBusinessUnitManager, isSuperAdmin, canAccessAccounting } from '../../utils/roleConstants';

/* Customer data fetchers (copied from QuotationDetails for consistency) */
async function fetchContactPersons(customerName) {
    if (!customerName) return [];
    const data = await getCustomersByName(customerName);
    return data
        .filter((r) => {
            const s = (r.status || r.Status || "").toLowerCase();
            return r.name === customerName && !!r.attention && s === "active";
        })
        .map((r) => r.attention ?? "")
        .filter(Boolean)
        .filter((v, i, a) => a.indexOf(v) === i);
}

async function fetchCustomerDetails(customerName) {
    if (!customerName) return {};
    const data = await getCustomersByName(customerName);
    const match = data.find((r) => r.name === customerName);
    if (!match) return {};
    return {
        paymentTerms: match.creditTerms || "",
        customerAddress: match.billingAddress || match.address || match.shippingAddress || "",
        shippingAddress: match.shippingAddress || "",
        phoneNumber: match.phoneNumber || match.phone_number || "",
    };
}

/* Sales Order specific extra field: Customer PO Number */
const salesOrderExtra = {
    left: ({ draft, onChange, errors, edit }) => (
        <>
            {/* Delivery Date */}
            {edit ? (
                <FieldInput
                    label="Delivery Date"
                    name="deliveryDate"
                    type="date"
                    value={draft?.deliveryDate || ""}
                    onChange={onChange}
                    /* Delivery cannot be before the order date (also checked on the server) */
                    min={draft?.date || undefined}
                    error={errors?.deliveryDate}
                    required
                />
            ) : (
                <InfoRow icon={FiCalendar} label="Delivery Date">
                    {draft?.deliveryDate || "—"}
                </InfoRow>
            )}
            {/* Customer PO Number */}
            {edit ? (
                <FieldInput
                    label="Customer PO Number"
                    name="custPONo"
                    value={draft?.custPONo || ""}
                    onChange={onChange}
                    placeholder="Enter PO number"
                    error={errors?.custPONo}
                />
            ) : (
                <InfoRow icon={FiHash} label="Customer PO Number">
                    {draft?.custPONo || "—"}
                </InfoRow>
            )}
        </>
    ),
    right: null,
};

export default function SalesOrderDetails() {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    // BUM can only view — hide create button
    const viewOnly = isBusinessUnitManager(user) && !canAccessAccounting(user) && !isSuperAdmin(user);
    const fetchCompProfile = useCallback(() => companyProfileService.getProfile(), []);

    return (
        <SalesDocDetails
            docType="Sales Order"
            basePath="/sales/sales-orders"
            fetchById={getSalesOrderById}
            updateItem={updateSalesOrder}
            createItem={createSalesOrder}
            deleteItem={deleteSalesOrder}
            fetchCustomerNames={getActiveCustomerCompanyNames}
            fetchContactPersons={fetchContactPersons}
            fetchCustomerDetails={fetchCustomerDetails}
            fetchCompanyProfile={fetchCompProfile}
            taxRate={0}
            statuses={["Draft", "Pending for Approval", "Approved", "Rejected"]}
            emptyDraft={{ custPONo: "", deliveryDate: "" }}
            // Delivery Date isn't tied to the customer; Customer PO Number is, so it still clears.
            keepOnCustomerChange={["deliveryDate"]}
            extraFields={salesOrderExtra}
            lineItemProps={{ searchable: true, searchSource: 'FG' }}
            hidePaymentTerms={false}
            viewOnly={viewOnly}
        />
    );
}
